import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { LogOut, RefreshCcw, Eye, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "dt_admin_token";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function Admin() {
  const [token, setToken] = React.useState(() => localStorage.getItem(TOKEN_KEY));
  const [bookings, setBookings] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [selected, setSelected] = React.useState(null);

  const fetchBookings = React.useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please log in.");
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    if (token) fetchBookings();
  }, [token, fetchBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked ${status}`);
      fetchBookings();
      setSelected(null);
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm("Delete this booking? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/bookings/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Booking deleted");
      fetchBookings();
      setSelected(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setBookings([]);
  };

  if (!token) {
    return <LoginScreen onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }

  const stats = {
    total: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_mobile-detail-pro-26/artifacts/st6pxnfk_2D5BD9E6-85C3-4BF5-B85B-9DAEBA92022E.png"
              alt="DT Detail"
              className="h-10 w-auto"
            />
            <span className="font-heading font-bold tracking-tight text-slate-500 text-sm">
              · Admin
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={fetchBookings} data-testid="admin-refresh">
              <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout">
              <LogOut className="w-4 h-4 mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-slate-900 mb-2">
          Bookings
        </h1>
        <p className="text-slate-600 mb-8">All appointments, newest first.</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total", value: stats.total },
            { label: "Pending", value: stats.pending },
            { label: "Confirmed", value: stats.confirmed },
            { label: "Completed", value: stats.completed },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
                {s.label}
              </div>
              <div className="font-heading font-bold text-3xl text-slate-900">
                {s.value}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Date / Time</TableHead>
                <TableHead>City</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && bookings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                    No bookings yet.
                  </TableCell>
                </TableRow>
              )}
              {bookings.map((b) => (
                <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{b.customer_name}</div>
                    <div className="text-xs text-slate-500">{b.customer_phone}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{b.service_label}</div>
                    <div className="text-xs text-slate-500">{b.price}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {b.vehicle_year} {b.vehicle_make} {b.vehicle_model}
                  </TableCell>
                  <TableCell className="text-sm">
                    {format(new Date(b.appointment_date), "MMM d")} · {b.appointment_time}
                  </TableCell>
                  <TableCell className="text-sm">{b.city}</TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_COLORS[b.status]} border font-medium capitalize`}>
                      {b.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSelected(b)}
                      data-testid={`view-booking-${b.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="booking-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {selected.customer_name}
                </DialogTitle>
              </DialogHeader>
              <div className="grid sm:grid-cols-2 gap-4 mt-4">
                <Field label="Service" value={`${selected.service_label} — ${selected.price}`} />
                <Field label="Vehicle Type" value={selected.vehicle_type} />
                <Field
                  label="Vehicle"
                  value={`${selected.vehicle_year} ${selected.vehicle_make} ${selected.vehicle_model}`}
                />
                <Field
                  label="Appointment"
                  value={`${format(new Date(selected.appointment_date), "EEEE, MMMM d")} · ${selected.appointment_time}`}
                />
                <Field label="Phone" value={selected.customer_phone} />
                <Field label="Email" value={selected.customer_email} />
                <div className="sm:col-span-2">
                  <Field label="Address" value={`${selected.address}, ${selected.city}`} />
                </div>
                {selected.notes && (
                  <div className="sm:col-span-2">
                    <Field label="Notes" value={selected.notes} />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-4 border-t border-slate-200">
                <Select
                  value={selected.status}
                  onValueChange={(v) => updateStatus(selected.id, v)}
                >
                  <SelectTrigger className="w-48" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => deleteBooking(selected.id)}
                  data-testid="delete-booking-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">
      {label}
    </div>
    <div className="text-slate-900 font-medium">{value}</div>
  </div>
);

const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      onLogin(res.data.token);
      toast.success("Welcome back");
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6" data-testid="admin-login-page">
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white p-8 rounded-xl border border-slate-200 shadow-sm"
      >
        <div className="flex items-center justify-center mb-6">
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-detail-pro-26/artifacts/st6pxnfk_2D5BD9E6-85C3-4BF5-B85B-9DAEBA92022E.png"
            alt="DT Detail"
            className="h-20 w-auto"
          />
        </div>
        <div className="text-center mb-6">
          <span className="font-heading font-semibold tracking-tight text-slate-500 text-sm uppercase">
            Admin Login
          </span>
        </div>
        <Label htmlFor="pw">Password</Label>
        <Input
          id="pw"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          data-testid="admin-password-input"
          className="mt-1.5"
        />
        <Button
          type="submit"
          disabled={loading || !password}
          data-testid="admin-login-submit"
          className="w-full mt-5 bg-[#4B0082] hover:bg-[#3B0066] text-white"
        >
          {loading ? "Logging in..." : "Log in"}
        </Button>
        <Link
          to="/"
          className="block text-center text-sm text-slate-500 hover:text-slate-700 mt-4"
        >
          ← Back to site
        </Link>
      </form>
    </div>
  );
};
